// src/components/VectorStoreExplorer.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const API_BASE = "/api/vectorstore"; // ajusta si tu API vive en otro host

// Inyecta estilos una sola vez
function useInjectStyles() {
  useEffect(() => {
    const id = "vsx-styles";
    if (document.getElementById(id)) return;
    const css = `
    .vsx-root{position:relative;padding:16px;user-select:none;cursor:default;}
    .vsx-tablewrap,.vsx-table td,.vsx-table th,.vsx-select,.vsx-input,.vsx-btn{user-select:text;}
    .vsx-header{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px;}
    .vsx-title{font-size:20px;font-weight:700;margin:0;}
    .vsx-actions{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
    .vsx-filters{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:10px}
    .vsx-sep{width:1px;height:28px;background:#e8e8e8;margin:0 4px;}
    .vsx-hidden-file{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0;padding:0;margin:-1px;}
    .vsx-field{display:inline-flex;align-items:center;gap:6px;}
    .vsx-label{font-size:12px;color:#666;}
    .vsx-select,.vsx-input{height:34px;border:1px solid #ddd;border-radius:10px;padding:4px 10px;background:#fff;outline:none;}
    .vsx-input{min-width:220px}
    .vsx-select:disabled,.vsx-input:disabled{opacity:.6;}
    .vsx-btn{height:36px;padding:0 12px;border-radius:999px;border:1px solid transparent;background:#f6f6f6;font-weight:600;line-height:36px;display:inline-flex;align-items:center;gap:8px;transition:transform .02s ease,background .2s ease,border-color .2s ease;}
    .vsx-btn:active{transform:translateY(1px);} .vsx-btn:disabled{opacity:.6;cursor:not-allowed;}
    .vsx-btn-primary{background:#0d6efd;color:#fff;border-color:#0d6efd;} .vsx-btn-primary:hover{background:#0b5ed7;border-color:#0b5ed7;}
    .vsx-btn-ghost{background:#fff;border-color:#ddd;} .vsx-btn-ghost:hover{background:#f7f7f7;}
    .vsx-btn-danger{background:#fff1f0;color:#d4380d;border-color:#ffd1cc;} .vsx-btn-danger:hover{background:#ffe2df;border-color:#ffc4bd;}
    .vsx-tablewrap{border:1px solid #eee;border-radius:12px;overflow:auto;background:#fff;}
    .vsx-table{width:100%;border-collapse:collapse;font-size:14px;background:#fff;}
    .vsx-table thead tr{background:#fafafa;}
    .vsx-table th,.vsx-table td{padding:10px 12px;border-top:1px solid #f1f1f1;vertical-align:middle;}
    .tl{text-align:left;} .tr{text-align:right;} .tc{text-align:center;}
    .mono{font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono",monospace;}
    .vsx-empty{color:#888;padding:24px 0;}
    .vsx-msg{margin-bottom:10px;padding:8px 12px;background:#f6f9ff;border:1px solid #e3ecff;color:#254a91;border-radius:10px;}
    .vsx-overlay{position:fixed;inset:0;background:rgba(255,255,255,0.6);display:flex;justify-content:center;align-items:center;z-index:50;pointer-events:all;}
    .vsx-spinner{width:44px;height:44px;border-radius:50%;border:4px solid #d6e4ff;border-top-color:#0d6efd;animation:vsx-spin .8s linear infinite;}
    .vsx-spinner-btn{width:16px;height:16px;border-radius:50%;border:2px solid #c0d6ff;border-top-color:#fff;animation:vsx-spin .8s linear infinite;display:inline-block;}
    @keyframes vsx-spin{to{transform:rotate(360deg)}}
    .vsx-count{font-size:12px;color:#666;margin-bottom:6px}

    /* Toast */
    .vsx-toast{position:fixed;right:16px;bottom:16px;z-index:60;display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:12px;color:#fff;box-shadow:0 10px 30px rgba(0,0,0,.15);}
    .vsx-toast.info{background:#0d6efd;}
    .vsx-toast.success{background:#198754;}
    .vsx-toast.error{background:#dc3545;}
    .vsx-toast .vsx-close{background:transparent;border:0;color:inherit;font-size:18px;line-height:1;cursor:pointer;padding:0 4px;opacity:.9}
    `;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = css;
    document.head.appendChild(style);
  }, []);
}

export default function VectorStoreExplorer() {
  useInjectStyles();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false); // carga de listado
  const [working, setWorking] = useState(false); // acciones (subir/borrar)
  const [msg, setMsg] = useState("");

  const [limit, setLimit] = useState(50);
  const [order, setOrder] = useState("desc");

  // Toast
  const [toast, setToast] = useState({ show: false, text: "", tone: "info" });
  function showToast(text, tone = "info", ms = 3000) {
    setToast({ show: true, text, tone });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(t => ({ ...t, show: false })), ms);
  }

  // Paginación por cursor
  const [after, setAfter] = useState(null);
  const [before, setBefore] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const canPrev = useMemo(() => Boolean(before), [before]);
  const canNext = useMemo(() => hasMore && Boolean(after), [hasMore, after]);

  // Filtros
  const [q, setQ] = useState("");           // buscar por nombre (substring)
  const [from, setFrom] = useState("");     // yyyy-mm-dd
  const [to, setTo] = useState("");         // yyyy-mm-dd

  // input file oculto
  const fileRef = useRef(null);
  const openFileDialog = () => fileRef.current?.click();

  async function fetchPage({ afterCursor, beforeCursor } = {}) {
    setLoading(true);
    setMsg("");
    try {
      const params = { limit, order, hydrate: 1 };
      if (afterCursor) params.after = afterCursor;
      if (beforeCursor) params.before = beforeCursor;

      const { data } = await axios.get(`${API_BASE}/files`, { params });

      const items = data.data || [];
      setRows(items);

      // Derivar cursores si faltan
      const derivedFirst = items.length ? items[0]?.id : null;
      const derivedLast  = items.length ? items[items.length - 1]?.id : null;
      const firstId = data.first_id ?? derivedFirst;
      const lastId  = data.last_id  ?? derivedLast;

      setAfter(data.has_more ? lastId : null); // next
      setBefore(firstId);                      // prev
      setHasMore(Boolean(data.has_more));
    } catch (e) {
      console.error(e);
      setMsg("Error al listar archivos.");
      showToast("Error al listar archivos.", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, order]);

  const onNext = () => after && fetchPage({ afterCursor: after });
  const onPrev = () => before && fetchPage({ beforeCursor: before });

  // Conversión de fecha a epoch (segundos) en local
  const toStartOfDayEpoch = (yyyy_mm_dd) => {
    const d = new Date(yyyy_mm_dd + "T00:00:00");
    return Math.floor(d.getTime() / 1000);
  };
  const toEndOfDayEpoch = (yyyy_mm_dd) => {
    const d = new Date(yyyy_mm_dd + "T23:59:59");
    return Math.floor(d.getTime() / 1000);
  };

  // Filtro local sobre la página actual
  const visibleRows = useMemo(() => {
    const qnorm = q.trim().toLowerCase();
    const fromEpoch = from ? toStartOfDayEpoch(from) : null;
    const toEpoch   = to ? toEndOfDayEpoch(to) : null;

    return rows.filter(r => {
      const name = (r.filename || "").toLowerCase();
      const inName = qnorm ? name.includes(qnorm) : true;

      const created = r.created_at || 0; // secs
      const inFrom = fromEpoch !== null ? created >= fromEpoch : true;
      const inTo   = toEpoch   !== null ? created <= toEpoch   : true;

      return inName && inFrom && inTo;
    });
  }, [rows, q, from, to]);

  async function handleUploadSelected(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setMsg("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      setWorking(true);
      const { data } = await axios.post(`${API_BASE}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const status = data?.vector_store_file?.status || "?";
      setMsg(`Subido: ${file.name} • estado: ${status}`);
      showToast(`Subido: ${file.name} • estado: ${status}`, "success");
      fetchPage();
    } catch (err) {
      console.error(err);
      setMsg("Error al subir el archivo.");
      showToast("Error al subir el archivo.", "error");
    } finally {
      setWorking(false);
      e.target.value = ""; // reset input
    }
  }

  // Descargar contenido parseado del Vector Store (TXT por defecto o JSON)
  function handleDownload(file, format = "txt") {
    const url = `${API_BASE}/files/${file.file_id}/content?format=${format}`;
    window.open(url, "_blank", "noopener");
  }

  async function handleDelete(file) {
    const name = file.filename || file.file_id;
    if (!window.confirm(`¿Eliminar del Vector Store?\n\n${name}`)) return;
    setMsg("");
    try {
      setWorking(true);
      await axios.delete(`${API_BASE}/files/${file.file_id}`, { params: { purge: 0 } });
      setMsg("Archivo eliminado del Vector Store.");
      showToast(`Eliminado del índice: ${name}`, "success");
      fetchPage();
    } catch (err) {
      console.error(err);
      setMsg("No se pudo eliminar.");
      showToast("No se pudo eliminar.", "error");
    } finally {
      setWorking(false);
    }
  }

  function clearFilters() {
    setQ(""); setFrom(""); setTo("");
  }

  return (
    <div className="vsx-root" contentEditable={false}>
      {(loading || working) && (
        <div className="vsx-overlay" role="status" aria-live="polite">
          <div className="vsx-spinner" aria-label="Cargando" />
        </div>
      )}

      <div className="vsx-header">
        <h2 className="vsx-title">Vector Store Explorer</h2>
        <div className="vsx-actions" aria-label="Acciones">
          {/* input file oculto + botón custom */}
          <input
            ref={fileRef}
            type="file"
            className="vsx-hidden-file"
            onChange={handleUploadSelected}
            tabIndex={-1}
          />
          <button
            type="button"
            className="vsx-btn vsx-btn-primary"
            onClick={openFileDialog}
            disabled={working || loading}
          >
            {working ? <span className="vsx-spinner-btn" /> : "Subir archivo…"}
          </button>

          <div className="vsx-sep" />

          <label className="vsx-field">
            <span className="vsx-label">Límite</span>
            <select
              className="vsx-select"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              disabled={loading || working}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={75}>75</option>
              <option value={100}>100</option>
            </select>
          </label>

          <label className="vsx-field">
            <span className="vsx-label">Orden</span>
            <select
              className="vsx-select"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              disabled={loading || working}
            >
              <option value="desc">desc</option>
              <option value="asc">asc</option>
            </select>
          </label>

          <div className="vsx-sep" />

          <button
            type="button"
            className="vsx-btn vsx-btn-ghost"
            onClick={onPrev}
            disabled={!canPrev || loading || working}
            title="Anterior"
          >
            ◀ Anterior
          </button>

          <button
            type="button"
            className="vsx-btn vsx-btn-ghost"
            onClick={onNext}
            disabled={!canNext || loading || working}
            title="Siguiente"
          >
            Siguiente ▶
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="vsx-filters">
        <label className="vsx-field" style={{minWidth: 280}}>
          <span className="vsx-label">Buscar</span>
          <input
            className="vsx-input"
            type="text"
            placeholder="Nombre de archivo…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            disabled={loading || working}
          />
        </label>

        <label className="vsx-field">
          <span className="vsx-label">Desde</span>
          <input
            className="vsx-input"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            disabled={loading || working}
          />
        </label>

        <label className="vsx-field">
          <span className="vsx-label">Hasta</span>
          <input
            className="vsx-input"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            disabled={loading || working}
          />
        </label>

        <button
          type="button"
          className="vsx-btn vsx-btn-ghost"
          onClick={clearFilters}
          disabled={loading || working || (!q && !from && !to)}
          title="Limpiar filtros"
        >
          Limpiar
        </button>
      </div>

      {/* Contador */}
      <div className="vsx-count">
        Mostrando <strong>{visibleRows.length}</strong> de {rows.length} (página)
      </div>

      <div className="vsx-tablewrap" role="region" aria-label="Listado de archivos">
        <table className="vsx-table">
          <thead>
            <tr>
              <th className="tl">Nombre</th>
              <th className="tl mono">File ID</th>
              <th className="tl">Status</th>
              <th className="tr">Bytes</th>
              <th className="tl">Creado</th>
              <th className="tc">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r) => (
              <tr key={r.id}>
                <td title={r.filename || ""}>{r.filename || "—"}</td>
                <td className="mono">{r.file_id || "—"}</td>
                <td>{r.status}</td>
                <td className="tr">{(r.bytes ?? r.usage_bytes) ?? ""}</td>
                <td>{r.created_at ? new Date(r.created_at * 1000).toLocaleString() : ""}</td>
                <td className="tc">
                  <button
                    className="vsx-btn vsx-btn-ghost"
                    onClick={() => handleDownload(r, "txt")}
                    disabled={working || loading}
                    title="Descargar contenido parseado (.txt)"
                  >
                    Descargar TXT
                  </button>{" "}
                  <button
                    className="vsx-btn vsx-btn-ghost"
                    onClick={() => handleDownload(r, "json")}
                    disabled={working || loading}
                    title="Descargar JSON del contenido parseado"
                  >
                    Descargar JSON
                  </button>{" "}
                  <button
                    className="vsx-btn vsx-btn-danger"
                    onClick={() => handleDelete(r)}
                    disabled={working || loading}
                    title="Eliminar del Vector Store"
                  >
                    {working ? <span className="vsx-spinner-btn" /> : "Eliminar"}
                  </button>
                </td>
              </tr>
            ))}
            {visibleRows.length === 0 && !loading && (
              <tr>
                <td colSpan="6" className="tc vsx-empty">Sin resultados</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Toast */}
      {toast.show && (
        <div className={`vsx-toast ${toast.tone}`} role="status" aria-live="polite">
          <span>{toast.text}</span>
          <button
            className="vsx-close"
            onClick={() => setToast(t => ({ ...t, show: false }))}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
